import { CCard, CCardBody, CCardHeader, CCol, CRow } from "@coreui/react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  Receipt,
  RECEIPT_STATUS_CANCELLATION_FAILED,
  RECEIPT_STATUS_CANCELLATION_REQUESTED,
  RECEIPT_STATUS_CANCELLED,
  RECEIPT_STATUS_CHOICES,
  RECEIPT_STATUS_ERROR,
  RECEIPT_STATUS_GENERATED,
  RECEIPT_PAYMENT_METHOD_CHOICES,
  RECEIPT_PAYMENT_METHOD_CHECK,
  formatReceiptNumber,
} from "../../models/receipt";
import { CLIENT_TYPE_CHOICES } from "../../models/client";
import {
  ASSOCIATED_DOCUMENT_TYPE_CHOICES,
  ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE,
} from "../../models/associated-document";
import { CURRENCY_CHOICES, PYG } from "../../currency/available-currencies";
import { formatToCurrency } from "../../currency/format";
import { formatUTCDateTime } from "../../utils/dates";
import { getItem } from "../../api/generics";
import { Invoice, formatInvoiceNumber } from "../../models/invoice";
import { SUCCESS } from "../../utils/constants/tags";

interface ReceiptDetailViewProps {
  receipt: Receipt;
}

const getStatusBadgeClassName = (status?: string): string => {
  if (status === RECEIPT_STATUS_ERROR) return "badge-danger";
  if (status === RECEIPT_STATUS_GENERATED) return "badge-success";
  if (status === RECEIPT_STATUS_CANCELLATION_REQUESTED) return "badge-light";
  if (status === RECEIPT_STATUS_CANCELLED) return "badge-info";
  if (status === RECEIPT_STATUS_CANCELLATION_FAILED) return "badge-danger";
  return "badge-info";
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <p className="mb-1">
    <strong>{label}:</strong> {children || "-"}
  </p>
);

const ReceiptDetailView: React.FC<ReceiptDetailViewProps> = ({ receipt }) => {
  const currency = receipt.currency || PYG;

  const isElectronicInvoice =
    receipt.associatedDocumentType ===
    ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE;

  const [associatedInvoice, setAssociatedInvoice] = useState<
    Invoice | undefined
  >(undefined);
  const [loadingAssociated, setLoadingAssociated] = useState(false);

  useEffect(() => {
    if (!isElectronicInvoice || !receipt.invoiceCdc) {
      setAssociatedInvoice(undefined);
      return;
    }
    const fetch = async () => {
      setLoadingAssociated(true);
      const params = new Map<string, string>();
      params.set("cdc", receipt.invoiceCdc!);
      const result = await getItem<Invoice>("/invoices/get_from_params/", params);
      if (result.status === SUCCESS && result.data) {
        setAssociatedInvoice(result.data);
      }
      setLoadingAssociated(false);
    };
    fetch();
  }, [isElectronicInvoice, receipt.invoiceCdc]);

  const clientAddress = [
    receipt.clientAddress,
    receipt.clientCityDescription,
    receipt.clientDistrictDescription,
    receipt.clientDepartmentDescription,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div>
      {/* Documento */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Documento</strong>
        </CCardHeader>
        <CCardBody>
          <CRow>
            {receipt.id && (
              <CCol md="6">
                <Field label="ID">
                  <strong>#{receipt.id}</strong>
                </Field>
              </CCol>
            )}
            {receipt.receiptNumber && (
              <CCol md="6">
                <Field label="Número de Recibo">
                  {formatReceiptNumber(receipt)}
                </Field>
              </CCol>
            )}
            <CCol md="6">
              <Field label="Timbrado">{receipt.stampIdentifier}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Establecimiento">{receipt.establishmentCode}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Punto de Expedición">
                {receipt.dispatchPointIdentifier}
              </Field>
            </CCol>
            <CCol md="6">
              <Field label="Fecha">
                {receipt.receiptDate
                  ? formatUTCDateTime(new Date(receipt.receiptDate))
                  : "-"}
              </Field>
            </CCol>
            <CCol md="6">
              <Field label="Estado">
                <span
                  className={`badge badge-pill ${getStatusBadgeClassName(receipt.status)}`}
                  style={{ fontSize: "13px" }}
                >
                  {RECEIPT_STATUS_CHOICES.get(receipt.status!) || receipt.status}
                </span>
              </Field>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {/* Documento Asociado */}
      {receipt.associatedDocumentType && (
        <CCard className="mb-3">
          <CCardHeader>
            <strong>Documento Asociado</strong>
          </CCardHeader>
          <CCardBody>
            {loadingAssociated ? (
              <div className="text-center py-2">
                <Spinner animation="border" size="sm" />
              </div>
            ) : (
              <CRow>
                <CCol md="6">
                  <Field label="Tipo">
                    {ASSOCIATED_DOCUMENT_TYPE_CHOICES.get(
                      receipt.associatedDocumentType,
                    ) ?? receipt.associatedDocumentType}
                  </Field>
                </CCol>
                {isElectronicInvoice && associatedInvoice ? (
                  <>
                    <CCol md="6">
                      <Field label="Número">
                        {associatedInvoice.invoiceNumber
                          ? formatInvoiceNumber(associatedInvoice)
                          : "-"}
                      </Field>
                    </CCol>
                    <CCol md="6">
                      <Field label="Timbrado">
                        {associatedInvoice.stampIdentifier}
                      </Field>
                    </CCol>
                    <CCol md="6">
                      <Field label="Fecha">
                        {associatedInvoice.invoiceDate
                          ? formatUTCDateTime(
                              new Date(associatedInvoice.invoiceDate),
                            )
                          : "-"}
                      </Field>
                    </CCol>
                    {associatedInvoice.cdc && (
                      <CCol md="12">
                        <Field label="CDC">
                          <code style={{ wordBreak: "break-all" }}>
                            {associatedInvoice.cdc}
                          </code>
                        </Field>
                      </CCol>
                    )}
                  </>
                ) : (
                  <>
                    <CCol md="6">
                      <Field label="Número">
                        {[
                          receipt.associatedDocumentEstablishmentCode,
                          receipt.associatedDocumentDispatchPointCode,
                          receipt.associatedDocumentNumber,
                        ]
                          .filter(Boolean)
                          .join("-") || "-"}
                      </Field>
                    </CCol>
                    <CCol md="6">
                      <Field label="Timbrado">
                        {receipt.associatedDocumentStampIdentifier}
                      </Field>
                    </CCol>
                    <CCol md="6">
                      <Field label="Fecha">
                        {receipt.associatedDocumentDate}
                      </Field>
                    </CCol>
                    {receipt.invoiceCdc && (
                      <CCol md="12">
                        <Field label="CDC">
                          <code style={{ wordBreak: "break-all" }}>
                            {receipt.invoiceCdc}
                          </code>
                        </Field>
                      </CCol>
                    )}
                  </>
                )}
              </CRow>
            )}
          </CCardBody>
        </CCard>
      )}

      {/* Contribuyente Emisor */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Contribuyente</strong>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="6">
              <Field label="RUC">{receipt.taxPayerRuc}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Razón Social">{receipt.taxPayerSocialName}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Nombre de Fantasía">
                {receipt.taxPayerFantasyName}
              </Field>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {/* Cliente */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Cliente</strong>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="6">
              <Field label="Razón Social">{receipt.clientSocialName}</Field>
            </CCol>
            <CCol md="6">
              <Field label="RUC">{receipt.clientRuc}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Tipo">
                {receipt.clientType
                  ? CLIENT_TYPE_CHOICES.get(receipt.clientType)
                  : "-"}
              </Field>
            </CCol>
            <CCol md="6">
              <Field label="Email">{receipt.clientEmail}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Teléfono">{receipt.clientPhoneNumber}</Field>
            </CCol>
            {clientAddress && (
              <CCol md="6">
                <Field label="Dirección">{clientAddress}</Field>
              </CCol>
            )}
          </CRow>
        </CCardBody>
      </CCard>

      {/* Pago */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Pago</strong>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="6">
              <Field label="Método de Pago">
                {receipt.paymentMethod
                  ? RECEIPT_PAYMENT_METHOD_CHOICES.get(receipt.paymentMethod)
                  : "-"}
              </Field>
            </CCol>
            <CCol md="6">
              <Field label="Moneda">
                {receipt.currency ? CURRENCY_CHOICES.get(receipt.currency) : "-"}
              </Field>
            </CCol>
            {receipt.paymentMethod === RECEIPT_PAYMENT_METHOD_CHECK && (
              <>
                <CCol md="6">
                  <Field label="Número de Cheque">{receipt.checkNumber}</Field>
                </CCol>
                <CCol md="6">
                  <Field label="Banco">{receipt.checkBank}</Field>
                </CCol>
              </>
            )}
            {receipt.obs && (
              <CCol md="12">
                <Field label="Observaciones">{receipt.obs}</Field>
              </CCol>
            )}
            {(receipt.paymentMethods ?? []).map((payment, index) => (
              <CCol md="12" key={payment.id ?? index}>
                <div className="border rounded p-2 mt-2">
                  <Field label={`Pago ${index + 1}`}>
                    {payment.description?.trim() || RECEIPT_PAYMENT_METHOD_CHOICES.get(payment.paymentMethod)} — {formatToCurrency(payment.amount, payment.currency)} {payment.currency}
                  </Field>
                  {payment.paymentMethod === RECEIPT_PAYMENT_METHOD_CHECK && (
                    <>
                      {payment.checkNumber && <Field label="Número de Cheque">{payment.checkNumber}</Field>}
                      {payment.checkBank && <Field label="Banco">{payment.checkBank}</Field>}
                    </>
                  )}
                </div>
              </CCol>
            ))}
          </CRow>
        </CCardBody>
      </CCard>

      {/* Descripción */}
      {receipt.description && (
        <CCard className="mb-3">
          <CCardHeader>
            <strong>Descripción</strong>
          </CCardHeader>
          <CCardBody>
            <p className="mb-0">{receipt.description}</p>
          </CCardBody>
        </CCard>
      )}

      {/* Ítems */}
      {(receipt.items ?? []).length > 0 && (
        <CCard className="mb-3">
          <CCardHeader>
            <strong>Ítems</strong>
          </CCardHeader>
          <CCardBody>
            <div className="table-responsive">
              <table className="table table-striped table-bordered table-sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Descripción</th>
                    <th>Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {receipt.items.map((item, index) => (
                    <tr key={item.id ?? index}>
                      <td>{index + 1}</td>
                      <td>{item.description || "-"}</td>
                      <td>{formatToCurrency(item.amount, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CCardBody>
        </CCard>
      )}

      {/* Total */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Total</strong>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="8" />
            <CCol md="4">
              <p className="mb-1">
                <strong>Total:</strong>{" "}
                <h5 className="d-inline">
                  {receipt.amount != null
                    ? formatToCurrency(receipt.amount, currency)
                    : "-"}
                </h5>
              </p>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default ReceiptDetailView;
